import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import { ONE_SHOT_HIGHLIGHT_CLASS, ONE_SHOT_HIGHLIGHT_TRANSITION } from '@/hooks/useOneShotHighlight';
import { cn } from '@/lib/utils';
import type { ClientRowView, ClientTotalsView } from '../lib/three-pl-usage-view';
import { CountCell, MoneyCell } from '@/components/shared/NumberCells';
import { NO_PRICE_FOCUS, Swatch } from './ThreePlCells';

interface ThreePlClientsTableProps {
  rows: ClientRowView[];
  footer: ClientTotalsView;
  ownerName: string;
  /** Formats in the report's currency (D2), never the viewer's. */
  formatValue: (value: number) => string;
  /** Opens that client's bill (sets the Client picker). */
  onOpen: (clientId: string) => void;
  /** The client row the total panel's "Not final" note just jumped to, tinted once (DESIGN.md §5); null otherwise. */
  highlightedRow: string | null;
}

/**
 * The count columns. Below `sm` they hide (C5), so Client, To bill and View
 * bill fit a phone with no sideways scroll; the order count stays under the
 * client's name.
 */
const COUNT_COL = 'hidden sm:table-cell';

/**
 * All clients, 3PL only: one full-width row per client. The whole row opens
 * the bill with the mouse; the visible "View bill" button is its keyboard
 * control, so the row itself is never a tab stop or a second button. To bill
 * is each client's bill Total and nothing else (one value per cell); owner
 * `skus.costs_manage` holders only, the Client cell says how many lines on that
 * bill have no price yet (D28, C4), the same count and unit as the total
 * panel's "Not final" note. The footer total carries no warning. Below `sm` the count columns
 * hide (C5).
 */
function ThreePlClientsTableImpl({
  rows,
  footer,
  ownerName,
  formatValue,
  onOpen,
  highlightedRow,
}: ThreePlClientsTableProps) {
  const { t } = useTranslation('inventory');
  const showRedone = footer.redone !== null;
  const th = 'px-3 py-2 text-xs font-medium text-muted-foreground align-bottom';

  return (
    <section className="rounded-lg border bg-card">
      {/* `relative`: the sr-only header text is absolutely positioned, so
          without a containing block here it escapes this scroller and pushes
          the whole page sideways on a phone. */}
      <div className="relative overflow-x-auto">
        <table className="w-full border-collapse text-sm sm:min-w-[44rem]">
          <thead>
            <tr className="border-b">
              <th scope="col" className={cn(th, 'text-left')}>
                {t('reporting.threePlUsage.client')}
              </th>
              <th scope="col" className={cn(th, COUNT_COL, 'text-right')}>
                {t('reporting.threePlUsage.clients.pieces')}
              </th>
              <th scope="col" className={cn(th, COUNT_COL, 'text-right')}>
                {t('reporting.threePlUsage.ownerStock', { owner: ownerName })}
              </th>
              <th scope="col" className={cn(th, COUNT_COL, 'text-right')}>
                {t('reporting.threePlUsage.notShipped')}
              </th>
              {showRedone && (
                <th scope="col" className={cn(th, COUNT_COL, 'text-right')}>
                  {t('reporting.threePlUsage.clients.redone', { owner: ownerName })}
                </th>
              )}
              <th scope="col" className={cn(th, 'text-right')}>
                {t('reporting.threePlUsage.clients.toBill')}
              </th>
              <th scope="col" className={cn(th, 'w-px')}>
                <span className="sr-only">{t('reporting.threePlUsage.clients.viewBill')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onOpen(row.id)}
                className={cn(
                  'cursor-pointer border-b last:border-0',
                  row.noPrice !== null && row.noPrice > 0 && ONE_SHOT_HIGHLIGHT_TRANSITION,
                  highlightedRow === row.id
                    ? ONE_SHOT_HIGHLIGHT_CLASS
                    : 'hover:bg-muted/40 motion-reduce:hover:bg-muted/40',
                )}
              >
                <td className="px-3 py-2.5">
                  {/* The name in full: it wraps beside its swatch on a phone. */}
                  <div className="flex items-start gap-2">
                    <span className="flex h-5 shrink-0 items-center">
                      <Swatch colour={row.colour} />
                    </span>
                    <span className="min-w-0 font-semibold [overflow-wrap:anywhere]">{row.name}</span>
                  </div>
                  <p className="pl-[1.125rem] text-xs text-muted-foreground tabular-nums">
                    {t('reporting.threePlUsage.count.orders', { count: row.orders })}
                  </p>
                  {row.noPrice !== null && row.noPrice > 0 && (
                    <div data-no-price={row.id} tabIndex={-1} className={cn('ml-[1.125rem] mt-0.5', NO_PRICE_FOCUS)}>
                      <NoteLine tone="warning" icon={AlertTriangle} className="tabular-nums">
                        {t('reporting.threePlUsage.clients.noPrice', { count: row.noPrice })}
                      </NoteLine>
                    </div>
                  )}
                </td>
                <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                  <CountCell as="span" value={row.pieces} strong />
                </td>
                <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                  <CountCell as="span" value={row.ownerStock} />
                </td>
                <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                  <CountCell as="span" value={row.notShipped} />
                </td>
                {showRedone && (
                  <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                    <CountCell as="span" value={row.redone ?? 0} />
                  </td>
                )}
                <td className="px-3 py-2.5 text-right">
                  <MoneyCell as="span" value={row.toBill} formatValue={formatValue} strong />
                </td>
                <td className="px-3 py-2.5 text-right">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 gap-1 px-2.5 text-xs"
                    aria-label={t('reporting.threePlUsage.clients.viewBillFor', { client: row.name })}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpen(row.id);
                    }}
                  >
                    {t('reporting.threePlUsage.clients.viewBill')}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t bg-muted/30">
              <th scope="row" className="px-3 py-2.5 text-left font-normal">
                <span className="font-semibold">{t('reporting.threePlUsage.allClients')}</span>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {t('reporting.threePlUsage.count.orders', { count: footer.orders })}
                </p>
              </th>
              <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                <CountCell as="span" value={footer.pieces} strong />
              </td>
              <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                <CountCell as="span" value={footer.ownerStock} />
              </td>
              <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                <CountCell as="span" value={footer.notShipped} />
              </td>
              {showRedone && (
                <td className={cn('px-3 py-2.5 text-right', COUNT_COL)}>
                  <CountCell as="span" value={footer.redone ?? 0} />
                </td>
              )}
              <td className="px-3 py-2.5 text-right">
                <MoneyCell as="span" value={footer.toBill} formatValue={formatValue} strong />
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

export const ThreePlClientsTable = React.memo(ThreePlClientsTableImpl);
